import React, { useEffect, useState } from 'react';
import { Avatar, List, Skeleton } from 'antd';
import StateEnum from '@constants/state.enum';
import useNotification from '@hooks/notification/useNotification';
import { StepsCounter, StepsElement } from '@models/steps';
import getAllSteps, { getActiveApplicants, getStepsCounter } from '@services/steps/steps-http.service';
import StepColumn from '@components/app-components/position/position-steps/step-column/StepColumn';
import { StepApplicantElement, StepApplicantsElement } from '@models/applicants';
import HireCard from '@components/app-components/position/position-steps/hire-card/HireCard';
import styles from './PositionSteps.module.scss';

export type PositionStepsProps = {
  positionId: string;
};

const PositionSteps: React.FC<PositionStepsProps> = ({ positionId }) => {
  const [steps, setSteps] = useState<StepsElement>();
  const [stepCounts, setStepCounts] = useState<StepsCounter>();
  const [applicants, setApplicants] = useState<StepApplicantsElement>();
  const [columnLoader, setColumnLoader] = useState<boolean>(true);
  const [hiredCandidates, setHiredCandidates] = useState<StepApplicantElement[]>([]);
  const [hiredCounts, setHiredCounts] = useState<number>(0);
  const [hiredLoader, setHiredLoader] = useState<boolean>(false);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const fetchSteps = async () => {
    try {
      setHiredLoader(true);
      setColumnLoader(true);
      const stepsElem: StepsElement = await getAllSteps(positionId);
      await setSteps(stepsElem);
      setColumnLoader(false);
      setHiredLoader(false);
    } catch (e: any) {
      generalError();
      setHiredLoader(false);
      setColumnLoader(false);
    }
  };

  const fetchStepsCounter = async () => {
    try {
      const stepCountElem: StepsCounter = await getStepsCounter(positionId);
      setStepCounts(stepCountElem);
    } catch (e: any) {
      generalError();
    }
  };

  const fetchApplicants = async () => {
    try {
      const stepApplicants: StepApplicantsElement = await getActiveApplicants(positionId);
      setApplicants(stepApplicants);
      setHiredCandidates(stepApplicants.items.filter((obj) => obj.hired === true));
      const countHiredApplicants = stepApplicants.items.reduce((count, obj) => {
        if (obj.hired === true) {
          return count + 1;
        }
        return count;
      }, 0);
      setHiredCounts(countHiredApplicants!);
    } catch (e: any) {
      generalError();
    }
  };

  useEffect(() => {
    fetchSteps();
    fetchApplicants();
    fetchStepsCounter();
  }, []);

  const getRejectedStepCounter = (stepCountsElem: StepsCounter, stepId: string) => {
    return stepCountsElem.find((obj) => {
      return obj.stepId === stepId;
    });
  };

  return (
    <div className={styles.wrapSteps}>
      {steps?.length &&
        steps.map((step, index) =>
          columnLoader ? (
            <Skeleton active avatar key={Number(step.id)}>
              <List.Item.Meta avatar={<Avatar />} />
            </Skeleton>
          ) : (
            applicants && (
              <StepColumn
                key={Number(step.id)}
                stepId={step.id}
                stepTitle={step.title}
                positionId={positionId}
                renderTime={index}
                applicants={applicants.items}
                fetchApplicants={() => fetchApplicants()}
                rejectedStepCount={stepCounts ? getRejectedStepCounter(stepCounts, step.id)!.rejectedCounter : 0}
                fetchRejectedCounter={() => fetchStepsCounter()}
                fetchSteps={() => fetchSteps()}
              />
            )
          ),
        )}
      <HireCard
        candidates={hiredCandidates}
        positionId={positionId}
        fetchApplicants={fetchApplicants}
        hiredCounts={hiredCounts}
        hiredLoader={hiredLoader}
      />
    </div>
  );
};

export default PositionSteps;
